import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFeedbackApi } from "@/lib/api/feedback/useFeedbackApi";
import { getFileSizeMB } from "@/lib/helpers/file";
import { useToast } from "@/lib/hooks";

import styles from "./FeedbackModal.module.scss";

import { RhfFormProvider } from "../RhfFormProvider/RhfFormProvider";
import Button from "../ui/Button";
import { RhfFileInput } from "../ui/FileInput/RhfFileInput";
import { Modal } from "../ui/Modal/Modal";
import { RhfSelect } from "../ui/Select/RhfSelect";
import { RhfTextAreaInput } from "../ui/TextAreaInput/RhfTextAreaInput";

export type FeedbackModalProps = {
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

type FeedbackFormValues = {
  description: string;
  category: string;
  attachments: File[];
};

export const FeedbackModal = ({
  isOpen,
  setOpen,
}: FeedbackModalProps): JSX.Element => {
  const { createFeedback } = useFeedbackApi();
  const { t } = useTranslation("translation");

  const { publish } = useToast();

  const methods = useForm<FeedbackFormValues>({
    defaultValues: {
      description: "",
      category: "OTHER",
      attachments: [],
    },
    mode: "all",
  });

  useEffect(() => {
    if (!isOpen) {
      methods.reset();
    }
  }, [isOpen]);

  const onSubmit = async (data: FeedbackFormValues) => {
    try {
      await createFeedback({
        ...data,
        pageUrl: window.location.href,
      });

      setOpen(false);

      publish({
        variant: "success",
        text: t("feedbackSubmittedSuccessfully"),
      });
    } catch (e) {
      console.error(e);
      publish({
        variant: "danger",
        text: t("somethingWentWrongTryAgain"),
      });
    }
  };

  return (
    <Modal
      title={t("submitSupportRequest")}
      isOpen={isOpen}
      setOpen={setOpen}
      size="auto"
      Trigger={<div />}
      CloseTrigger={<div />}
    >
      <div className={styles.modal_wrapper}>
        <RhfFormProvider
          methods={methods}
          onSubmit={onSubmit}
          className="flex flex-col gap-5 pt-4 justify-center align-center"
        >
          <RhfSelect
            name="category"
            label={t("category")}
            emptyLabel={t("selectCategory")}
            options={[
              {
                value: "OTHER",
                label: t("other"),
              },
              {
                value: "FEATURE_REQUEST",
                label: t("featureRequest"),
              },
              {
                value: "BUG_REPORT",
                label: t("bugReport"),
              },
              {
                value: "PERFOMANCE_ISSUE",
                label: t("performanceIssue"),
              },
              {
                value: "EXTRACTED_DATA_ISSUE",
                label: t("unexpectedAiModelResult"),
              },
            ]}
          />

          <RhfTextAreaInput
            name="description"
            placeholder={t("descriptionRequiredPlaceholder")}
            rhfOptions={{
              required: t("descriptionIsRequired"),
            }}
          />

          <RhfFileInput
            label={t("attachments")}
            name={"attachments"}
            multiple
            acceptedMimeTypes={[
              "image/*",
              "application/pdf",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ]}
            rhfOptions={{
              validate: (files: File[]) => {
                if (files.length > 5) {
                  return t("maxFilesAllowed", { max: 5 });
                }

                for (const file of files) {
                  const fileSizeMB = getFileSizeMB(file);
                  if (fileSizeMB > 5) {
                    return t("fileExceedsSizeLimit", {
                      fileName: file.name,
                      limit: 5,
                    });
                  }
                }
              },
            }}
          />

          <Button
            disabled={!methods.formState.isValid}
            type="submit"
            isLoading={methods.formState.isSubmitting}
            className="mt-3"
            variant="primary"
            fullWidth
          >
            {t("submitSupportRequest")}
          </Button>
        </RhfFormProvider>
      </div>
    </Modal>
  );
};
